import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { X } from 'lucide-react';
import { usePushNotificationsContext } from '@/services/providers/use-push-notifications-context.ts';

interface NotificationPermissionProps {
  vapidPublicKey?: string;
  className?: string;
}

export const NotificationPermission: React.FC<NotificationPermissionProps> = ({
  className,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const {
    isSubscribed,
    isLoading,
    status,
    subscribe,
    clearStatus,
    arePushNotificationsAllowed,
    setArePushNotificationsAllowed,
  } = usePushNotificationsContext();

  useEffect(() => {
    if (
      arePushNotificationsAllowed === undefined &&
      (!status?.permission || !status?.subscription || !isSubscribed)
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsVisible(true);
    }
  }, []);

  const handleRequestPermission = async () => {
    clearStatus();
    await subscribe();
  };

  const handleDeny = () => {
    setIsVisible(false);
    setArePushNotificationsAllowed(false);
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  if (!('Notification' in window) || !isVisible) {
    return null;
  }

  return (
    <div className={className}>
      <Card className={`w-full max-w-md ${className}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-md">
              {status?.title ?? 'Enable Notifications'}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={handleDismiss} className="h-6 w-6 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <CardDescription>
            {status?.description || 'Enable notifications and stay up-to-date'}
          </CardDescription>
          {status?.type === 'error' && (
            <div className="flex gap-2">
              <Button
                onClick={handleRequestPermission}
                className="flex-1 bg-red-400 hover:bg-red-500"
                disabled={isLoading}
              >
                {isLoading ? 'Retrying...' : 'Try Again'}
              </Button>
              <Button variant="secondary" onClick={handleDismiss} className="flex-1">
                Dismiss
              </Button>
            </div>
          )}
          {status?.type === 'success' && !status?.permission && !status?.subscription && (
            <div className="flex gap-2">
              <Button
                onClick={handleRequestPermission}
                className="flex-1"
                disabled={isLoading}
              >
                {isLoading ? 'Setting up...' : 'Allow'}
              </Button>
              <Button variant="secondary" onClick={handleDeny} className="flex-1">
                Don't Allow
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
